import React, { useEffect, useState } from 'react';
import Api from '../../services/Api';
import StatusHelper from '../../helpers/StatusHelper';
import { useSalesHistoryConfig } from '../../hooks/SalesHistoryConfigContext';
import { useStorefrontSettings } from '../../hooks/StorefrontSettingsContext';
import Notifications from '../../services/Notifications';
import PDFIcon from '../icons/OrderAckIcon';
import WhatsAppIcon from '../icons/WhatsAppIcon';
import Loading from '../Loading';
import { GridToolbarContainer, GridToolbarFilterButton, GridToolbarExport, GridToolbarDensitySelector } from '@mui/x-data-grid-premium';
import Money from '../Money';
import DateDisplay from '../DateDisplay';
import DateHelper from "../../helpers/DateHelper";
import PricingHelper from '../../helpers/PricingHelper';
import { Link } from 'react-router-dom';
import TranslatableDataGrid from '../shared/TranslatableDataGrid';
import { ThemeProvider } from '@mui/material/styles';
import StorefrontAdminTheme from '../shared/MUIStyles/StorefrontAdminTheme';
import Paper from '@mui/material/Paper';
import { useStorefrontAdministrationConfig } from '../../hooks/StorefrontAdministrationConfigContext';
import Box from '@mui/material/Box';




const OrderLines = (props) => {
    const salesHistoryConfig = useSalesHistoryConfig();
    const localized = salesHistoryConfig.labels;
    const storefrontConfig = useStorefrontSettings();
    const dateDisplayFormat = storefrontConfig.settingsList.DateDisplayFormat;
    const gridLocalized = useStorefrontAdministrationConfig().labels;
    const [rows, setRows] = useState([]);
    const [csvData, setCSVData] = useState([]);
    const theme = StorefrontAdminTheme;
    const excludeStatuses = [
        'Shipped',
        'Complete',
        'Cancelled',
        'PartiallyShipped',
        'ReadyToInvoice',
        'Invoiced'
    ];
    const [fileExistenceData, setFileExistenceData] = useState([]);
    const [isFileCheckComplete, setIsFileCheckComplete] = useState(false);

    useEffect(() => {
        if (props.csvOrders) {
            const formattedData = formatCSVData(props.csvOrders);
            setCSVData(formattedData);
        }

        if (props.orderLines ) {
            const orders = mapOrderlinesToOrders(props.orderLines); 
            checkOrderFileExists(orders);
        }
    }, [props.csvOrders, props.orderLines]);

    const customToolbar = () => {
        return (
            <GridToolbarContainer  >
                <Box sx={{ flexGrow: 1 }} />
                <GridToolbarFilterButton
                    slotProps={{
                        tooltip: { title: 'Filter list' }
                    }}
                    variant="outlined"
                    color="update"

                />
                <GridToolbarDensitySelector
                    slotProps={{
                        tooltip: { title: 'Change density' }
                    }}
                    variant="outlined"
                    color="update"
                />

                <GridToolbarExport
                    printOptions={{ disableToolbarButton: true }}
                    slotProps={{
                        tooltip: { title: 'Export data' }
                    }}
                    variant="outlined"
                    color="update"
                />
            </GridToolbarContainer>
        );
    }

    const mapOrderlinesToOrders = (orderLines) => {
        var rows = [];
        orderLines.forEach((orderLine, index) => {
            rows.push({
                id: index,
                orderId: orderLine.orderId,
                referenceNumber: orderLine.referenceNumber,
                orderDate: orderLine.orderDate,
                requestDate: orderLine.requestDate,
                estimatedDate: orderLine.estimatedDate,
                shipDate: orderLine.shipDate,
                productSku: orderLine.productSku,
                itemDescription: orderLine.lineDescription,
                quantity: orderLine.quantity,
                quantityBackordered: orderLine.quantityBackordered,
                unitPrice_Amount: orderLine.unitPrice_Amount,
                price_Amount: orderLine.price_Amount,
                price_Currency: orderLine.price_Currency,
                lineStatus: orderLine.lineStatus,
                invoiceNumber: orderLine.invoiceNumber,
                billToId: orderLine.billToId,
                orderType: orderLine.orderType,
                orderCompany: orderLine.orderCompany
            });
        });
        setRows(rows);
        const orders = orderLines.map(orderLine => ({
            FileNumber: orderLine.orderId,
            DocumentType: orderLine.orderType,
            BillToId: orderLine.billToId,
            FileName: `${orderLine.billToId}-${orderLine.orderId}${orderLine.orderType}.pdf`
        }));
        return { FileDetails: orders };
    };

    const formatCSVData = (data) => {
        const formattedData = JSON.parse(JSON.stringify(data));
        formattedData.forEach((item) => {
            item.unitPrice_Amount = PricingHelper.formatNumber(props.selectedCustomer.address.country, item.unitPrice_Amount);
            item.price_Amount = PricingHelper.formatNumber(props.selectedCustomer.address.country, item.price_Amount);
            item.price_Currency = undefined;
            if (item.orderDate) {
                item.orderDate = DateHelper.format(item.orderDate, "yyyy-MMM-dd");
            }
            if (item.requestDate) {
                item.requestDate = DateHelper.format(item.requestDate, "yyyy-MMM-dd");
            }
            if (item.estimatedDate) {
                item.estimatedDate = DateHelper.format(item.estimatedDate, "yyyy-MMM-dd");
            }
            if (item.estimatedShipDate) {
                item.estimatedShipDate = DateHelper.format(item.estimatedShipDate, "yyyy-MMM-dd");
            }
            if (item.lineStatus) {
                item.lineStatus = StatusHelper.formatStatus(item.lineStatus, props.localized);
            }
            if (item.productDescription) {
                item.productDescription = item.productDescription.replace('"', '');
            }
        });
        return formattedData;
    };

    const checkOrderFileExists = async (mappedOrders) => {
        if (mappedOrders.FileDetails.length > 0) {
            const response = await Api.checkOrderFileExists(`/api/orders/checkOrderFileExists`, { FileDetails: mappedOrders.FileDetails });
            if (response.ok) {
                const data = await response.json();
                setFileExistenceData(data);
                setIsFileCheckComplete(true);
            }
        } else {
            setIsFileCheckComplete(true);
        }
    };

    const getPDF = (orderNumber, orderType, billToId) => {
        Api.downloadFileName(`/api/orders/GetPDF/${billToId}/${orderNumber}/${orderType}`, `${billToId}-${orderNumber}${orderType}`)
            .then(handleError);
    };

    const getPDFLink = (orderNumber, orderType, billToId) => {
        Api.fetch(`/api/orders/GetPDFShareLink/${billToId}/${orderNumber}/${orderType}`)
            .then((res) => {
                window.open(res.shareUrl, '_blank');
            });
    };

    const handleError = (res) => {
        if (!res) Notifications.error(localized.NoPDFMessage);
    };

    const columns = [
        {
            field: 'orderId',
            headerName: props.localized.OrderNumber,
            headerAlign: 'left',
            renderCell: (params) => {
                return (
                    <Link to={`/order/${params.row.orderId}/${params.row.orderCompany}/${params.row.orderType}`}>
                        {params.row.orderId}
                    </Link>
                )
            }
        },
        {
            field: 'referenceNumber',
            headerName: props.localized.ReferenceNumber,
            headerAlign: 'left',
        },
        {
            field: 'orderDate',
            headerName: props.localized.OrderDate,
            headerAlign: 'left',
            renderCell: (params) => {
                return (
                    <DateDisplay date={params.row.orderDate} dateFormat={dateDisplayFormat} />
                )
            }
        },
        {
            field: 'requestDate',
            headerName: props.localized.RequestDate,
            headerAlign: 'left',
            renderCell: (params) => {
                return (
                    <DateDisplay date={params.row.requestDate} dateFormat={dateDisplayFormat} />
                )
            }

        },
        {
            field: 'estimatedDate',
            headerName: props.localized.ShipDate,
            headerAlign: 'left',
            renderCell: (params) => {
                return (
                    props.showEstimatedShipDate &&
                    <>
                        {!excludeStatuses.includes(params.row.lineStatus) 
                            ? <>
                                {(DateHelper.compareDates(params.row.estimatedDate, params.row.orderDate) === -1)
                                    ? <div>NA</div>
                                    : <DateDisplay date={params.row.estimatedDate} dateFormat={dateDisplayFormat} />
                                }
                            </>
                            : <>
                                {(DateHelper.compareDates(params.row.shipDate, params.row.orderDate) === -1)
                                    ? <div>NA</div>
                                    : <DateDisplay date={params.row.shipDate} dateFormat={dateDisplayFormat} />
                                }
                            </>
                        }
                    </>
                )
            }
        },
        {
            field: 'productSku',
            headerName: props.localized.ItemNumber,
            headerAlign: 'left',
        },
        {
            field: 'itemDescription',
            headerName: props.localized.ItemDescription,
            headerAlign: 'left',
            flex: 1
        },
        {
            field: 'quantity',
            headerName: props.localized.QtyLabel,
            headerAlign: 'left',
        },
        {
            field: 'quantityBackordered',
            headerName: props.localized.QtyBackOrderedLabel,
            headerAlign: 'left',
        },
        {
            field: 'unitPrice_Amount',
            headerName: props.localized.UnitPrice,
            headerAlign: 'right',
            align: 'right',
            renderCell: (params) => {
                return (
                    <Money value={params.row.unitPrice_Amount} currency={params.row.price_Currency}  country={props.selectedCustomer.address.country} />
                )
            }
        },
        {
            field: 'price_Amount',
            headerName: props.localized.ProductTotal,
            headerAlign: 'right',
            align: 'right',
            renderCell: (params) => {
                return (
                    <Money value={params.row.price_Amount} currency={params.row.price_Currency} country={props.selectedCustomer.address.country} />
                )
            }
        },
        {
            field: 'lineStatus',
            headerName: props.localized.Status,
            headerAlign: 'left',
            renderCell: (params) => {
                return (
                    <div >{StatusHelper.formatStatus(params.row.lineStatus, props.localized)}</div>
                )
            }
        },
        {
            field: 'invoiceNumber',
            headerName: props.localized.InvoiceNumber,
            headerAlign: 'left'
        },
        {
            field: 'openLines',
            headerName: "PDF",
            headerAlign: 'left',
            renderCell: (params) => {
                const fileData = fileExistenceData.find(file => String(file.fileNumber).trim() == String(params.row.orderId).trim() && file.exists == true);
                const showIcons = fileData ? fileData.exists : false;
                return (
                    props.ordersByLinePDF && (
                    <strong>
                        <div className="br-invoice-pdf d-inline-flex" style={{ fontSize: '18px' }}>
                    <div
                        style={{
                            cursor: showIcons ? 'pointer' : 'not-allowed',
                            paddingRight: '14px',
                            opacity: showIcons ? 1 : 0.5,
                        }}
                        aria-disabled={!showIcons}
                        onClick={() => {
                            if (showIcons) {
                                getPDF(params.row.orderId, params.row.orderType, params.row.billToId);
                            }
                        }}
                        title={showIcons ? localized.DownloadPDF : localized.NoPDFMessage}
                    >
                        <PDFIcon />
                                </div>
                                {props.shareViaWhatsApp && (
                                    <div
                                        style={{
                                            cursor: showIcons ? 'pointer' : 'not-allowed',
                                            opacity: showIcons ? 1 : 0.5,
                                        }}
                                        aria-disabled={!showIcons}
                                        onClick={() => {
                                            if (showIcons) {
                                                getPDFLink(params.row.orderId, params.row.orderType, params.row.billToId);
                                            }
                                        }}
                                        title={showIcons ? localized.DownloadPDF : localized.NoPDFMessage}
                                    >
                                        <WhatsAppIcon />
                                    </div>
                                )}</div>
                    </strong>
                    )
                )
            },
            disableClickEventBubbling: true
        }
    ];
    return (
        <>
            {
                props.fetching && <Loading type="brand" />
            }
            {
                !props.fetching && (gridLocalized && Object.keys(gridLocalized).length > 0) &&
                <>
                    <ThemeProvider theme={theme}>
                        <Paper sx={{
                            height: 700,
                            width: '100%'
                        }}>
                            <TranslatableDataGrid
                                columnVisibilityModel={!props.ordersByLinePDF ? { openLines: false } : {}}
                                rows={rows}
                                columns={columns}
                                insetToolbar={customToolbar}
                                fetching={props.fetching}
                                labels={gridLocalized}
                                square={true}
                            />
                        </Paper>
                    </ThemeProvider>
                </>
            }
        </>
    );
};

export default OrderLines;
